import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, CheckCircle, AlertCircle, Lock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const formSchema = z.object({
  companyName: z.string().min(2, "Company name is required"),
  staffCount: z.coerce.number().min(1, "Must be at least 1").max(9, "Maximum 9 employees for micro business packages"),
  contactName: z.string().min(2, "Contact name is required"),
  contactEmail: z.string().email("Valid email required"),
  phone: z.string().optional(),
  primaryDomain: z.string().optional(),
  emailPlatform: z.enum(["microsoft_365", "google_workspace", "other"]),
  deviceMix: z.enum(["windows", "mac", "mixed"]),
  itManagement: z.enum(["internal", "outsourced", "none"]),
  mfaStatus: z.enum(["yes", "no", "unsure"]),
  encryptionStatus: z.enum(["yes", "no", "unsure"]),
  patchingStatus: z.enum(["auto", "manual", "unsure"]),
});

type FormData = z.infer<typeof formSchema>;

const CyberEssentialsMicroOnboarding = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [orderValid, setOrderValid] = useState(false);
  const [customerEmail, setCustomerEmail] = useState("");

  const orderId = searchParams.get("order_id");

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      companyName: "",
      staffCount: 1,
      contactName: "",
      contactEmail: "",
      phone: "",
      primaryDomain: "",
      emailPlatform: "microsoft_365",
      deviceMix: "windows",
      itManagement: "internal",
      mfaStatus: "unsure",
      encryptionStatus: "unsure",
      patchingStatus: "unsure",
    },
  });

  useEffect(() => {
    const checkOrder = async () => {
      if (!orderId) {
        setLoading(false);
        return;
      }

      try {
        // Use secure edge function instead of direct database query
        const { data, error } = await supabase.functions.invoke("validate-ce-order", {
          body: { orderId },
        });

        if (error) {
          console.error("Order validation error:", error);
          setLoading(false);
          return;
        }

        if (data?.valid) {
          setOrderValid(true);
          if (data.customerEmail) {
            setCustomerEmail(data.customerEmail);
            form.setValue("contactEmail", data.customerEmail);
          }
          if (data.onboardingSubmitted) {
            setSubmitted(true);
          }
        }
      } catch (err) {
        console.error("Order check error:", err);
      } finally {
        setLoading(false);
      }
    };

    checkOrder();
  }, [orderId, form]);

  const onSubmit = async (data: FormData) => {
    if (!orderId) return;

    setSubmitting(true);
    try {
      const { error } = await supabase.functions.invoke("submit-ce-onboarding", {
        body: {
          orderId,
          ...data,
        },
      });

      if (error) throw error;
      setSubmitted(true);
      toast.success("Onboarding submitted successfully!");
    } catch (err) {
      console.error("Submit error:", err);
      toast.error("Failed to submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <main className="pt-24 pb-16">
          <div className="container mx-auto px-4">
            <div className="max-w-xl mx-auto text-center py-12">
              <Loader2 className="w-8 h-8 mx-auto animate-spin text-accent" />
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!orderId || !orderValid) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <main className="pt-24 pb-16">
          <div className="container mx-auto px-4">
            <div className="max-w-xl mx-auto">
              <Card>
                <CardContent className="py-12 text-center">
                  <Lock className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <h1 className="text-xl font-bold mb-2">Onboarding Form Locked</h1>
                  <p className="text-muted-foreground mb-6">
                    This onboarding form unlocks after payment. Please choose a package to continue.
                  </p>
                  <Button onClick={() => navigate("/services/cyber-essentials-micro")}>
                    View packages
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <main className="pt-24 pb-16">
          <div className="container mx-auto px-4">
            <div className="max-w-xl mx-auto">
              <Card>
                <CardContent className="py-12 text-center">
                  <CheckCircle className="w-16 h-16 mx-auto text-green-500 mb-4" />
                  <h1 className="text-2xl font-bold mb-2">Onboarding Complete!</h1>
                  <p className="text-muted-foreground mb-6">
                    Thanks for completing your onboarding. We've received your information and will be in touch within 1 working day.
                  </p>
                  <Button variant="outline" onClick={() => navigate("/")}>
                    Return to homepage
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="pt-24 pb-16">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto">
            <Card>
              <CardHeader>
                <CardTitle>Cyber Essentials Onboarding</CardTitle>
                <CardDescription>
                  Complete this form to get started. It takes about 5 minutes.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <FormField control={form.control} name="companyName" render={({ field }) => (
                        <FormItem>
                          <FormLabel>Legal Company Name *</FormLabel>
                          <FormControl><Input {...field} /></FormControl>
                          <FormMessage />
                        </FormItem>
                      )} />
                      <FormField control={form.control} name="staffCount" render={({ field }) => (
                        <FormItem>
                          <FormLabel>Number of Staff (1-9) *</FormLabel>
                          <FormControl><Input type="number" min={1} max={9} {...field} /></FormControl>
                          <FormMessage />
                        </FormItem>
                      )} />
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                      <FormField control={form.control} name="contactName" render={({ field }) => (
                        <FormItem>
                          <FormLabel>Contact Name *</FormLabel>
                          <FormControl><Input {...field} /></FormControl>
                          <FormMessage />
                        </FormItem>
                      )} />
                      <FormField control={form.control} name="contactEmail" render={({ field }) => (
                        <FormItem>
                          <FormLabel>Contact Email *</FormLabel>
                          <FormControl><Input type="email" {...field} /></FormControl>
                          <FormMessage />
                        </FormItem>
                      )} />
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                      <FormField control={form.control} name="phone" render={({ field }) => (
                        <FormItem>
                          <FormLabel>Phone (optional)</FormLabel>
                          <FormControl><Input {...field} /></FormControl>
                          <FormMessage />
                        </FormItem>
                      )} />
                      <FormField control={form.control} name="primaryDomain" render={({ field }) => (
                        <FormItem>
                          <FormLabel>Primary Domain (optional)</FormLabel>
                          <FormControl><Input placeholder="example.co.uk" {...field} /></FormControl>
                          <FormMessage />
                        </FormItem>
                      )} />
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                      <FormField control={form.control} name="emailPlatform" render={({ field }) => (
                        <FormItem>
                          <FormLabel>Email Platform *</FormLabel>
                          <Select onValueChange={field.onChange} defaultValue={field.value}>
                            <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                            <SelectContent>
                              <SelectItem value="microsoft_365">Microsoft 365</SelectItem>
                              <SelectItem value="google_workspace">Google Workspace</SelectItem>
                              <SelectItem value="other">Other</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )} />
                      <FormField control={form.control} name="deviceMix" render={({ field }) => (
                        <FormItem>
                          <FormLabel>Device Mix *</FormLabel>
                          <Select onValueChange={field.onChange} defaultValue={field.value}>
                            <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                            <SelectContent>
                              <SelectItem value="windows">Windows only</SelectItem>
                              <SelectItem value="mac">Mac only</SelectItem>
                              <SelectItem value="mixed">Mixed</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )} />
                    </div>

                    <FormField control={form.control} name="itManagement" render={({ field }) => (
                      <FormItem>
                        <FormLabel>Who manages your IT? *</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                          <SelectContent>
                            <SelectItem value="internal">Internal team</SelectItem>
                            <SelectItem value="outsourced">Outsourced provider</SelectItem>
                            <SelectItem value="none">No dedicated IT</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )} />

                    <div className="grid sm:grid-cols-3 gap-4">
                      <FormField control={form.control} name="mfaStatus" render={({ field }) => (
                        <FormItem>
                          <FormLabel>MFA enabled? *</FormLabel>
                          <Select onValueChange={field.onChange} defaultValue={field.value}>
                            <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                            <SelectContent>
                              <SelectItem value="yes">Yes</SelectItem>
                              <SelectItem value="no">No</SelectItem>
                              <SelectItem value="unsure">Unsure</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )} />
                      <FormField control={form.control} name="encryptionStatus" render={({ field }) => (
                        <FormItem>
                          <FormLabel>Encryption on? *</FormLabel>
                          <Select onValueChange={field.onChange} defaultValue={field.value}>
                            <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                            <SelectContent>
                              <SelectItem value="yes">Yes</SelectItem>
                              <SelectItem value="no">No</SelectItem>
                              <SelectItem value="unsure">Unsure</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )} />
                      <FormField control={form.control} name="patchingStatus" render={({ field }) => (
                        <FormItem>
                          <FormLabel>Patching? *</FormLabel>
                          <Select onValueChange={field.onChange} defaultValue={field.value}>
                            <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                            <SelectContent>
                              <SelectItem value="auto">Automatic</SelectItem>
                              <SelectItem value="manual">Manual</SelectItem>
                              <SelectItem value="unsure">Unsure</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )} />
                    </div>

                    <Button type="submit" className="w-full bg-accent hover:bg-accent/90" disabled={submitting}>
                      {submitting ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Submitting...
                        </>
                      ) : (
                        "Submit Onboarding"
                      )}
                    </Button>
                  </form>
                </Form>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default CyberEssentialsMicroOnboarding;
